const axios = require('axios');
let { getInstance } = require('./auth');

const isLocal = process.env.VUE_APP_ENV === 'local';
const VUE_APP_AUTH_BASE_URL = process.env.VUE_APP_AUTH_BASE_URL;

let BASE_URL;
if (process.env.NODE_ENV !== 'production') {
  console.log('NODE_ENV', process.env.NODE_ENV);
}

if (process.env.NODE_ENV === 'test') {
  BASE_URL = 'http://localhost:3000/dev';
} else {
  BASE_URL = process.env.VUE_APP_BASE_URL;
}

let headers = {
  Accept: 'application/json',
  'x-api-key': process.env.VUE_APP_API_KEY,
};

let axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: headers,
  withCredentials: true,
});

let refreshPromise = null;

const refreshTokens = async () => {
  const requestBody = isLocal
    ? {
        refresh_token: localStorage.getItem('refreshToken'),
        returnTokens: true,
      }
    : {};

  const response = await axios.post(
    `${VUE_APP_AUTH_BASE_URL}/authorization/refresh-token`,
    requestBody,
    { withCredentials: true }
  );

  if (isLocal && response.data) {
    const tokenMap = {
      accessToken: 'access_token',
      refreshToken: 'refresh_token',
      idToken: 'id_token',
    };
    Object.entries(tokenMap).forEach(([localKey, responseKey]) => {
      const value = response.data[responseKey];
      if (value) {
        localStorage.setItem(localKey, value);
      }
    });
  }

  return response;
};

axiosInstance.interceptors.request.use(
  (config) => {
    if (!isLocal) {
      return config;
    }

    const accessToken = localStorage.getItem('accessToken');

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    if (!isLocal) {
      return response;
    }

    const tokenFields = {
      accessToken: ['access_token', 'x-access-token', 'access-token'],
      refreshToken: ['refresh_token', 'x-refresh-token', 'refresh-token'],
      idToken: ['id_token'],
    };

    Object.entries(tokenFields).forEach(([key, possibleNames]) => {
      for (const name of possibleNames) {
        const fromBody = response.data?.[name];
        const fromHeader = response.headers?.[name];
        const value = fromBody || fromHeader;

        if (value) {
          localStorage.setItem(key, value);
          break;
        }
      }
    });

    return response;
  },
  (error) => {
    const originalConfig = error.config;
    const status = error.response?.status;

    if (!originalConfig || (status !== 401 && status !== 403)) {
      return Promise.reject(error);
    }

    const requestUrl = originalConfig.url || '';
    const isAuthEndpoint =
      requestUrl.includes('/authorization') ||
      requestUrl.includes('/sso/') ||
      requestUrl.includes('/entra-login') ||
      requestUrl.includes('/otp-check') ||
      requestUrl.includes('/refresh-token');

    if (isAuthEndpoint) {
      return Promise.reject(error);
    }

    if (status === 403) {
      const currentUrl = new URL(location.href);
      const params = `${currentUrl.pathname}${currentUrl.search}`;
      getInstance().forbidden(params);
      return Promise.reject(error);
    }

    if (originalConfig._retry) {
      console.log('Token refresh failed, logging out...');
      getInstance().logout();
      return Promise.reject(error);
    }

    originalConfig._retry = true;

    if (!refreshPromise) {
      refreshPromise = refreshTokens().finally(() => {
        refreshPromise = null;
      });
    }

    return refreshPromise
      .then(() => {
        if (!isLocal) {
          return axiosInstance(originalConfig);
        }

        const newAccessToken = localStorage.getItem('accessToken');

        if (newAccessToken) {
          originalConfig.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        return axiosInstance(originalConfig);
      })
      .catch(() => {
        console.log('Token refresh failed, logging out...');
        getInstance().logout();
        return Promise.reject(error);
      });
  }
);

module.exports = axiosInstance;
